<template>
    <!--
        Agency details / edit page chrome — TAG portal refresh handoff
        (rJFDyGoM_RZ868hGojealA bundle). Supports both layouts via the
        `variant` prop:

          variant="a" — Sectioned form + sticky nav:
            TopNav → [left rail: breadcrumb + "On this page" + completion]
                   → [main: sticky header (avatar/name/status/actions) + tabs + slot]

          variant="b" — Inline-edit (Attio/Linear feel):
            TopNav → breadcrumb → [left profile card] + [right: tabs + slot]

        In both cases the default <slot/> holds the existing legacy form +
        sub-Vue components untouched. The shell only owns the chrome. The
        Save button uses [type=submit form="agency-edit-form"] so the page's
        single <form id="agency-edit-form"> submits exactly as before.
    -->
    <div class="tag-app" :style="appStyle">
        <TopNav active-id="agencies" />

        <!-- ===================== VARIANT A ===================== -->
        <div v-if="variant === 'a'" :style="bodyStyle">
            <aside :style="railStyle">
                <div :style="breadcrumbStyle">
                    <a :href="agenciesHref" :style="breadcrumbLinkStyle">Agencies</a>
                    <span :style="breadcrumbSepStyle">›</span>
                    <span :style="breadcrumbCurrentStyle">{{ agencyName }}</span>
                </div>

                <div :style="railEyebrowStyle">On this page</div>

                <a
                    v-for="(s, i) in sections"
                    :key="s.id"
                    :href="'#' + s.id"
                    :style="sectionLinkStyle(i === activeSectionIndex)"
                    @click="onSectionClick(s.id)"
                >
                    <span :style="sectionIconStyle(i === activeSectionIndex)">
                        <Icon :name="s.icon" />
                    </span>
                    {{ s.label }}
                </a>

                <div :style="completionCardStyle">
                    <div :style="profileEyebrowStyle">Profile completion</div>
                    <div :style="profileScoreLineStyle">
                        <span class="tag-mono tag-tnum" :style="profileScoreStyle">{{ completion }}</span>
                        <span :style="profileScoreOfStyle">/ 100</span>
                    </div>
                    <div :style="profileTrackStyle">
                        <div :style="profileFillStyle" />
                    </div>
                    <div v-if="completionHint" :style="profileHintStyle">{{ completionHint }}</div>
                </div>
            </aside>

            <div :style="mainStyle">
                <div :style="stickyHeaderStyle">
                    <div :style="stickyHeaderRowStyle">
                        <Avatar :name="agencyName" :size="44" />
                        <div :style="headerCentreStyle">
                            <div :style="headerTitleRowStyle">
                                <h1 :style="headerH1Style">{{ agencyName }}</h1>
                                <AgencyPill v-if="agencyStatus" :status="agencyStatus" />
                                <a v-if="referralHref" :href="referralHref" :style="referralLinkStyle">{{ referralLabel }}</a>
                            </div>
                            <div :style="headerSubtitleStyle">
                                <span v-if="agencyWebsite" :style="subtitleItemStyle">
                                    <Icon name="link" />
                                    <a :href="agencyWebsite" :style="subtitleLinkStyle" target="_blank" rel="noopener">{{ websiteDisplay }}</a>
                                </span>
                                <span v-if="agencyCity" :style="subtitleSepStyle">·</span>
                                <span v-if="agencyCity">{{ agencyCity }}</span>
                                <span v-if="agencyCreated" :style="subtitleSepStyle">·</span>
                                <span v-if="agencyCreated">Created <span class="tag-mono">{{ agencyCreated }}</span></span>
                                <span v-if="activeDealsCount != null" :style="subtitleSepStyle">·</span>
                                <span v-if="activeDealsCount != null">{{ activeDealsCount }} active deal{{ activeDealsCount === 1 ? '' : 's' }}</span>
                            </div>
                        </div>
                        <div :style="headerActionsStyle">
                            <slot name="actions">
                                <ActionsMenu v-if="actions.length" :actions="actions" align="right" />
                                <button type="submit" :form="formId" class="btn btn--primary btn--sm" :style="{ gap: '5px' }">
                                    <Icon name="check" /> Save
                                </button>
                            </slot>
                        </div>
                    </div>

                    <div :style="tabsRowStyle">
                        <a
                            v-for="(t, i) in tabs"
                            :key="t.id"
                            :href="'#tab-' + t.id"
                            :style="tabLinkStyle(i === activeTabIndex)"
                            @click.prevent="onTabClick(t.id, i)"
                        >
                            {{ t.label }}
                            <span v-if="i === activeTabIndex" :style="tabActiveBarStyle" aria-hidden="true" />
                        </a>
                    </div>
                </div>

                <div :style="bodyContentStyle"><slot /></div>
            </div>
        </div>

        <!-- ===================== VARIANT B ===================== -->
        <div v-else :style="bBodyStyle">
            <div :style="bBreadcrumbStyle">
                <a :href="agenciesHref" :style="breadcrumbLinkStyle">Agencies</a>
                <span :style="breadcrumbSepStyle">›</span>
                <span :style="breadcrumbCurrentStyle">{{ agencyName }}</span>
            </div>

            <div :style="bTwoColStyle">
                <!-- Left: sticky profile card -->
                <aside class="surface" :style="bProfileCardStyle">
                    <Avatar :name="agencyName" :size="48" />
                    <div :style="{ marginTop: '10px' }">
                        <h1 :style="bProfileNameStyle">{{ agencyName }}</h1>
                        <div :style="bProfileMetaStyle">
                            <AgencyPill v-if="agencyStatus" :status="agencyStatus" />
                            <span v-if="agencyWebsite" :style="bProfileMetaRowStyle">
                                <Icon name="link" />
                                <a :href="agencyWebsite" :style="subtitleLinkStyle" target="_blank" rel="noopener">{{ websiteDisplay }}</a>
                            </span>
                            <span v-if="agencyCity" :style="bProfileMetaRowStyle">
                                <Icon name="building" /> {{ agencyCity }}
                            </span>
                        </div>
                    </div>

                    <div :style="bDividerStyle" />

                    <div :style="bMiniGridStyle">
                        <div v-if="activeDealsCount != null">
                            <div :style="bMiniLabelStyle">Deals (active)</div>
                            <div :style="bMiniValueStyle">{{ activeDealsCount }}</div>
                        </div>
                        <div v-if="revenueYtd">
                            <div :style="bMiniLabelStyle">Revenue YTD</div>
                            <div :style="bMiniValueStyle">{{ revenueYtd }}</div>
                        </div>
                        <div v-if="agencyCreated">
                            <div :style="bMiniLabelStyle">Created</div>
                            <div class="tag-mono tag-tnum" :style="bMiniValueStyle">{{ agencyCreated }}</div>
                        </div>
                        <div v-if="ownerName">
                            <div :style="bMiniLabelStyle">Owner</div>
                            <div :style="bMiniValueStyle">
                                <Avatar :name="ownerName" :size="16" /> {{ ownerName }}
                            </div>
                        </div>
                    </div>

                    <div :style="bDividerStyle" />

                    <!-- Primary form action lives here in B (no top Save bar). -->
                    <button type="submit" :form="formId" class="btn btn--primary btn--sm" :style="bCardBtnStyle">
                        <Icon name="check" /> Save changes
                    </button>
                    <a v-if="quickbooksHref" :href="quickbooksHref" target="_blank" rel="noopener" class="btn btn--sm" :style="bCardSecondaryBtnStyle">
                        Open in QuickBooks ↗
                    </a>
                    <a v-if="referralHref" :href="referralHref" class="btn btn--ghost btn--sm" :style="bCardReferralBtnStyle">
                        {{ referralLabel }}
                    </a>
                </aside>

                <!-- Right: tabs + form body -->
                <div :style="bRightColStyle">
                    <div :style="bTabsRowStyle">
                        <a
                            v-for="(t, i) in tabs"
                            :key="t.id"
                            :href="'#tab-' + t.id"
                            :style="bTabLinkStyle(i === activeTabIndex)"
                            @click.prevent="onTabClick(t.id, i)"
                        >
                            {{ t.label }}
                            <span v-if="i === activeTabIndex" :style="bTabActiveBarStyle" aria-hidden="true" />
                        </a>
                        <span :style="{ flex: '1 1 auto' }" />
                        <div v-if="actions.length" :style="{ paddingBottom: '6px' }">
                            <ActionsMenu :actions="actions" align="right" />
                        </div>
                    </div>
                    <div :style="bSlotWrapStyle"><slot /></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import TopNav from "../Agencies/_atoms/TopNav.vue";
import Avatar from "../Agencies/_atoms/Avatar.vue";
import AgencyPill from "../Agencies/_atoms/AgencyPill.vue";
import Icon from "../Agencies/_atoms/Icon.vue";
import ActionsMenu from "../Agencies/_atoms/ActionsMenu.vue";

const DEFAULT_TABS = [
    { id: "details",         label: "Details" },
    { id: "business",        label: "Services & expertise" },
    { id: "finance",         label: "Financials" },
    { id: "address",         label: "Address" },
    { id: "contacts",        label: "Contacts" },
    { id: "documents",       label: "Documents" },
    { id: "google_response", label: "Form responses" },
];

const DEFAULT_SECTIONS = [
    { id: "identity",   label: "Identity",          icon: "user" },
    { id: "category",   label: "Industry & niche",  icon: "spark" },
    { id: "biz",        label: "Business",          icon: "building" },
    { id: "financials", label: "Financials",        icon: "trending" },
    { id: "audience",   label: "Client audience",   icon: "user" },
    { id: "geo",        label: "Locations",         icon: "globe" },
    { id: "notes",      label: "Notes",             icon: "edit" },
];

export default {
    name: "AgencyEditShell",
    components: { TopNav, Avatar, AgencyPill, Icon, ActionsMenu },
    props: {
        // "a" = sectioned form + sticky nav, "b" = inline-edit profile card.
        variant: { type: String, default: "b", validator: (v) => ["a", "b"].includes(v) },
        agencyName: { type: String, required: true },
        agencyStatus: { type: String, default: "" },
        agencyWebsite: { type: String, default: "" },
        agencyCity: { type: String, default: "" },
        agencyCreated: { type: String, default: "" },
        activeDealsCount: { type: [Number, String], default: null },
        agenciesHref: { type: String, default: "/agencies" },
        referralHref: { type: String, default: "" },
        referralLabel: { type: String, default: "+ Referral partner" },
        formId: { type: String, default: "agency-edit-form" },
        completion: { type: Number, default: 0 },
        completionHint: { type: String, default: "" },
        // Variant-B profile-card extras (all optional — hidden when empty).
        quickbooksHref: { type: String, default: "" },
        revenueYtd: { type: String, default: "" },
        ownerName: { type: String, default: "" },
        tabs: { type: Array, default: () => DEFAULT_TABS },
        sections: { type: Array, default: () => DEFAULT_SECTIONS },
        initialTab: { type: String, default: "details" },
        // Actions dropdown items — [{ id, label, icon, href, meta, hint,
        // accent, muted, kbd:[], divider }]. Built by the blade from the
        // real routes (View deals / Add deal / View sales / Add legacy).
        actions: { type: Array, default: () => [] },
    },
    data() {
        return {
            activeTabIndex: this.findTabIndex(this.initialTab),
            activeSectionIndex: 0,
        };
    },
    computed: {
        websiteDisplay() {
            const url = (this.agencyWebsite || "")
                .replace(/^https?:\/\//i, "")
                .replace(/^www\./i, "")
                .replace(/\/$/, "");
            return url || this.agencyWebsite;
        },
        // ===== Shared =====
        appStyle() {
            return {
                height: "100vh",
                background: "var(--bg)",
                color: "var(--text)",
                fontFamily: "var(--font-sans)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            };
        },
        // ===== Variant A styles =====
        bodyStyle() {
            return { display: "flex", flex: "1 1 auto", minHeight: 0 };
        },
        railStyle() {
            return {
                width: "232px",
                flex: "0 0 232px",
                borderRight: "1px solid var(--border-faint)",
                padding: "20px 16px",
                background: "var(--surface)",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                overflow: "auto",
            };
        },
        breadcrumbStyle() {
            return {
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
                color: "var(--text-muted)",
                marginBottom: "14px",
            };
        },
        breadcrumbLinkStyle() {
            return { color: "var(--text-muted)", textDecoration: "none" };
        },
        breadcrumbSepStyle() {
            return { color: "var(--text-faint)" };
        },
        breadcrumbCurrentStyle() {
            return {
                color: "var(--text)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "150px",
            };
        },
        railEyebrowStyle() {
            return {
                fontSize: "11px",
                fontWeight: 500,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--text-faint)",
                padding: "0 6px 6px",
            };
        },
        completionCardStyle() {
            return {
                marginTop: "16px",
                padding: "12px",
                background: "var(--surface-2)",
                border: "1px solid var(--border-faint)",
                borderRadius: "var(--r-md)",
            };
        },
        profileEyebrowStyle() {
            return {
                fontSize: "11px",
                fontWeight: 500,
                color: "var(--text-muted)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: "6px",
            };
        },
        profileScoreLineStyle() {
            return { display: "flex", alignItems: "baseline", gap: "4px", marginBottom: "6px" };
        },
        profileScoreStyle() {
            return { fontSize: "20px", fontWeight: 500 };
        },
        profileScoreOfStyle() {
            return { fontSize: "11px", color: "var(--text-muted)" };
        },
        profileTrackStyle() {
            return { height: "4px", background: "var(--border)", borderRadius: "2px", overflow: "hidden" };
        },
        profileFillStyle() {
            const pct = Math.max(0, Math.min(100, this.completion || 0));
            return { height: "100%", width: pct + "%", background: "var(--tag-red)", borderRadius: "2px" };
        },
        profileHintStyle() {
            return { fontSize: "11px", color: "var(--text-muted)", marginTop: "8px", lineHeight: 1.4 };
        },
        mainStyle() {
            return {
                flex: "1 1 auto",
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                background: "var(--bg)",
            };
        },
        stickyHeaderStyle() {
            return { position: "sticky", top: 0, zIndex: 2, background: "var(--bg)", padding: "18px 28px 0" };
        },
        stickyHeaderRowStyle() {
            return {
                display: "flex",
                alignItems: "center",
                gap: "14px",
                paddingBottom: "14px",
                borderBottom: "1px solid var(--border-faint)",
            };
        },
        headerCentreStyle() {
            return { flex: "1 1 auto", minWidth: 0 };
        },
        headerTitleRowStyle() {
            return { display: "flex", alignItems: "center", gap: "8px", marginBottom: "2px" };
        },
        headerH1Style() {
            return { font: "600 22px/1.1 var(--font-display)", letterSpacing: "-0.015em", margin: 0 };
        },
        referralLinkStyle() {
            return { color: "var(--tag-red)", fontSize: "12px", fontWeight: 500, textDecoration: "none", padding: "2px 6px" };
        },
        headerSubtitleStyle() {
            return {
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "12px",
                color: "var(--text-muted)",
                flexWrap: "wrap",
            };
        },
        subtitleItemStyle() {
            return { display: "inline-flex", alignItems: "center", gap: "5px" };
        },
        subtitleLinkStyle() {
            return { color: "var(--text-muted)", textDecoration: "none" };
        },
        subtitleSepStyle() {
            return { color: "var(--text-faint)" };
        },
        headerActionsStyle() {
            return { display: "flex", alignItems: "center", gap: "8px" };
        },
        tabsRowStyle() {
            return { display: "flex", gap: "2px", paddingTop: "8px" };
        },
        tabActiveBarStyle() {
            return {
                position: "absolute",
                left: "8px",
                right: "8px",
                bottom: "-1px",
                height: "2px",
                background: "var(--tag-red)",
                borderRadius: "2px",
            };
        },
        bodyContentStyle() {
            return { padding: "24px 28px 48px" };
        },
        // ===== Variant B styles =====
        // Body is a non-scrolling column: breadcrumb pinned on top, the
        // two-column row fills the rest. Scrolling is delegated to the
        // RIGHT column only — so the left profile card stays put for the
        // entire scroll instead of sliding away near the bottom (which
        // happens with position:sticky once the container's end is hit).
        bBodyStyle() {
            return {
                flex: "1 1 auto",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                minHeight: 0,
                overflow: "hidden",
            };
        },
        bBreadcrumbStyle() {
            return {
                flex: "0 0 auto",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
                color: "var(--text-muted)",
                padding: "16px 24px 12px",
            };
        },
        bTwoColStyle() {
            return {
                padding: "0 24px 0",
                display: "flex",
                gap: "24px",
                flex: "1 1 auto",
                minHeight: 0,
                alignItems: "stretch",
                overflow: "hidden",
            };
        },
        bProfileCardStyle() {
            return {
                flex: "0 0 280px",
                padding: "18px",
                // Pinned to the top of the (non-scrolling) row. If the card
                // ever grows taller than the viewport it scrolls inside
                // itself rather than clipping.
                alignSelf: "flex-start",
                maxHeight: "100%",
                overflow: "auto",
                marginBottom: "24px",
            };
        },
        bProfileNameStyle() {
            return { font: "600 17px/1.2 var(--font-display)", letterSpacing: "-0.015em", margin: "0 0 4px" };
        },
        bProfileMetaStyle() {
            return { display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px", color: "var(--text-muted)" };
        },
        bProfileMetaRowStyle() {
            return { display: "inline-flex", alignItems: "center", gap: "5px" };
        },
        bDividerStyle() {
            return { height: "1px", background: "var(--border-faint)", margin: "14px -18px" };
        },
        bMiniGridStyle() {
            return { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" };
        },
        bMiniLabelStyle() {
            return {
                fontSize: "10.5px",
                color: "var(--text-muted)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: "3px",
            };
        },
        bMiniValueStyle() {
            return { fontSize: "13px", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: "5px" };
        },
        bCardBtnStyle() {
            return { width: "100%", gap: "5px" };
        },
        bCardSecondaryBtnStyle() {
            return { width: "100%", marginTop: "6px", textDecoration: "none" };
        },
        bCardReferralBtnStyle() {
            return { width: "100%", marginTop: "6px", color: "var(--tag-red)", textDecoration: "none" };
        },
        bRightColStyle() {
            // The only scroll container in variant B. Tabs strip stays at
            // the top while the form body below it scrolls.
            return {
                flex: "1 1 auto",
                minWidth: 0,
                overflow: "auto",
                height: "100%",
                display: "flex",
                flexDirection: "column",
            };
        },
        bTabsRowStyle() {
            return {
                flex: "0 0 auto",
                position: "sticky",
                top: 0,
                zIndex: 1,
                background: "var(--bg)",
                display: "flex",
                alignItems: "center",
                gap: 0,
                borderBottom: "1px solid var(--border-faint)",
                marginBottom: "14px",
            };
        },
        bSlotWrapStyle() {
            return { paddingBottom: "32px" };
        },
    },
    methods: {
        findTabIndex(id) {
            const idx = this.tabs.findIndex((t) => t.id === id);
            return idx >= 0 ? idx : 0;
        },
        sectionLinkStyle(active) {
            return {
                display: "flex",
                alignItems: "center",
                gap: "9px",
                padding: "6px 9px",
                borderRadius: "6px",
                fontSize: "13px",
                color: active ? "var(--text)" : "var(--text-muted)",
                background: active ? "var(--hover-strong)" : "transparent",
                textDecoration: "none",
                fontWeight: active ? 500 : 400,
            };
        },
        sectionIconStyle(active) {
            return { color: active ? "var(--tag-red)" : "var(--text-faint)", display: "inline-flex" };
        },
        tabLinkStyle(active) {
            return {
                position: "relative",
                padding: "10px 14px",
                fontSize: "13px",
                fontWeight: 500,
                color: active ? "var(--text)" : "var(--text-muted)",
                textDecoration: "none",
                borderRadius: "6px 6px 0 0",
            };
        },
        bTabLinkStyle(active) {
            return {
                position: "relative",
                padding: "10px 14px",
                fontSize: "13px",
                fontWeight: 500,
                color: active ? "var(--text)" : "var(--text-muted)",
                textDecoration: "none",
            };
        },
        bTabActiveBarStyle() {
            return {
                position: "absolute",
                left: "10px",
                right: "10px",
                bottom: "-1px",
                height: "2px",
                background: "var(--tag-red)",
                borderRadius: "2px",
            };
        },
        onSectionClick(id) {
            const idx = this.sections.findIndex((s) => s.id === id);
            if (idx >= 0) this.activeSectionIndex = idx;
        },
        onTabClick(id, index) {
            this.activeTabIndex = index;
            const trigger = document.querySelector(`.js-btn-tab-nav[data-tab="${id}"]`);
            if (trigger && typeof trigger.click === "function") trigger.click();
            this.$emit("tab-change", { id, index });
        },
    },
};
</script>
